import { memo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
// Local files
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useLocations from 'hooks/useLocations';
import useApp from 'hooks/useApp';
import useSuccess from 'hooks/useSuccess';
import { DELETE_LOCATION } from 'helpers/confirmations';
import { checkEmptyString } from 'helpers';
import { locationsSelectors } from 'features/locations';
import { appSelectors } from 'features/app'

const LocationsDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const { openDeleteDialog, setLocationDrawer } = useApp();
  const { createLocation, updateLocation } = useLocations();
  const { setSuccess } = useSuccess();
  const fetchedLocation = useCustomSelector(locationsSelectors.getLocationData);
  const { open, edit } = useCustomSelector(appSelectors.getLocationDrawerData);
  const fetching = useCustomSelector(locationsSelectors.locationIsFetching);
  const [name, setName] = useState({ value: '', error: '' });
  const [displayName, setDisplayName] = useState({ value: '', error: '' });
  const [address, setAddress] = useState({ value: '', error: '' });

  const handleClose = () => setLocationDrawer({ open: false, edit: false });
  const handleEntering = () => {
    if (edit) {
      setName({ value: fetchedLocation?.name, error: '' });
      setDisplayName({ value: fetchedLocation?.display_name ?? '', error: '' });
      setAddress({ value: fetchedLocation?.address ?? '', error: '' });
    } 
  };
  const handleExited = () => { 
    setName({ value: '', error: '' });
    setDisplayName({ value: '', error: '' });
    setAddress({ value: '', error: '' });
  };
  const needToUpdate = (a,b) => a.name !== b.name || a.display_name !== b.display_name || a.address !== b.address;
  const handleSave = () => {
    let hasError = false;
    if (!checkEmptyString(name.value)) {
      setName(prev => ({ ...prev, error: `Name can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(address.value)) {
      setAddress(prev => ({ ...prev, error: `Address can't be empty` }) );
      hasError = true;
    }
    if (hasError) return;

    const location = { 
      ...(edit && { id: fetchedLocation.id }),
      name: name.value,
      display_name: displayName.value,
      address: address.value
    };

    if (edit) {
      if (needToUpdate(fetchedLocation, location)) {
        updateLocation(location)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Location successfully updated');
            handleClose();
          }
        })
      } else {  
        handleClose();
      }
    } else {
      createLocation(location)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Location successfully created');
          handleClose();
        } 
      })
    }
  };
  
  const handleDelete = () => {
    handleClose();
    openDeleteDialog({ id: fetchedLocation.id, type: DELETE_LOCATION });
  };

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      onClose={handleClose}
      SlideProps={{ onExited: handleExited, onEntering: handleEntering }}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
          {edit && <Button color='error' disabled={fetching} onClick={handleDelete} sx={{ fontWeight: 700 }}>Delete</Button>}
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
          {!edit ? 'Create' : 'Edit'} Location
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        <TextField
          fullWidth
          margin='normal'
          label='Name'
          disabled={fetching}
          value={name.value}
          onChange={({ target: { value } }) => setName({ value, error: '' })}
          helperText={name.error}
          error={!!name.error}
        />
        <TextField
          fullWidth
          margin='normal'
          label='Display Name'
          disabled={fetching}
          value={displayName.value}
          onChange={({ target: { value } }) => setDisplayName({ value, error: '' })}
          helperText={displayName.error}
          error={!!displayName.error}
        />
        <TextField
          fullWidth
          margin='normal'
          label='Address'
          disabled={fetching}
          value={address.value}
          onChange={({ target: { value } }) => setAddress({ value, error: '' })}
          helperText={address.error}
          error={!!address.error}
        />
        <Button
          variant='contained'
          sx={{ display: 'flex', mx: 'auto', px: 5, mt: 3 }}
          disabled={fetching}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <LocationsDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);